import {Constants} from '@/utils/constants/commonConstants';
import {logger} from '@/helpers/logger';

export const matchesActivityStatus = (status, expected) =>
  (status ?? '').trim().toLowerCase() === expected.trim().toLowerCase();

export const formatActivityTypeForReview = (activityType) =>
  activityType ? String(activityType).toUpperCase() : '-';

/** Unpublish action button is clickable */
export const isActivityUnpublishEnabledStatus = (status) =>
  matchesActivityStatus(status, Constants.Published) ||
  matchesActivityStatus(status, Constants.InProgress);

/** Publish action button is clickable (Unpublished only; Draft uses disabled toggle) */
export const isActivityPublishEnabledStatus = (status) =>
  matchesActivityStatus(status, Constants.Unpublished) ||
  matchesActivityStatus(status, 'Inactive') ||
  matchesActivityStatus(status, 'In-Active');

/** Activity review Edit buttons hidden for Completed and Unpublished */
export const isActivityReviewEditable = (status) =>
  !matchesActivityStatus(status, Constants.Completed) &&
  !matchesActivityStatus(status, Constants.Unpublished);

export const TOGGLE_ACTION = {
  PUBLISH: 'publish',
  UNPUBLISH: 'unpublish',
  NONE: 'none'
};

/** Single publish/unpublish toggle: label, icon, and enabled state by activity status */
export const getActivityPublishToggleAction = (status) => {
  if (isActivityUnpublishEnabledStatus(status)) {
    return {
      action: TOGGLE_ACTION.UNPUBLISH,
      label: 'Unpublish',
      icon: 'pause',
      enabled: true
    };
  }
  if (isActivityPublishEnabledStatus(status)) {
    return {
      action: TOGGLE_ACTION.PUBLISH,
      label: 'Publish',
      icon: 'refresh',
      enabled: true
    };
  }
  if (matchesActivityStatus(status, Constants.Completed)) {
    return {
      action: TOGGLE_ACTION.NONE,
      label: 'Publish',
      icon: 'refresh',
      enabled: false
    };
  }
  if (matchesActivityStatus(status, Constants.Draft)) {
    return {
      action: TOGGLE_ACTION.NONE,
      label: 'Unpublish',
      icon: 'pause',
      enabled: false
    };
  }
  return {
    action: TOGGLE_ACTION.NONE,
    label: 'Unpublish',
    icon: 'pause',
    enabled: false
  };
};

const ACTIVITY_DUPLICATE_ENABLED_STATUSES = [
  Constants.Published,
  Constants.InProgress,
  Constants.Completed
];

export const isActivityDuplicateEnabledStatus = (status) =>
  ACTIVITY_DUPLICATE_ENABLED_STATUSES.some((expected) =>
    matchesActivityStatus(status, expected)
  );

export const isActivityDownloadDisabledStatus = (status) =>
  matchesActivityStatus(status, Constants.Draft) ||
  matchesActivityStatus(status, Constants.Published);

// Backward-compatible aliases; prefer activity-generic exports above.
export const SURVEY_TOGGLE_ACTION = TOGGLE_ACTION;
export const isSurveyUnpublishEnabledStatus = isActivityUnpublishEnabledStatus;
export const isSurveyPublishEnabledStatus = isActivityPublishEnabledStatus;
export const getSurveyPublishToggleAction = getActivityPublishToggleAction;
export const isSurveyDuplicateEnabledStatus = isActivityDuplicateEnabledStatus;
export const isSurveyDownloadDisabledStatus = isActivityDownloadDisabledStatus;

/** Save Draft allowed only for brand-new activities (no status) or Draft status. */
export const isActivitySaveDraftEnabled = (status) => {
  if (status == null || String(status).trim() === '') {
    return true;
  }
  return matchesActivityStatus(status, Constants.Draft);
};

/**
 * Merges PATCH activity status response into form data for Redux.
 * Uses the status field directly from the API response.
 */
export const mapActivityStatusFromPatchResponse = (
  response,
  currentFormData
) => {
  const patch =
    response?.data != null && typeof response.data === 'object'
      ? response.data
      : response;

  const updates = {};

  if (patch?.isactive != null) {
    updates.isactive = patch.isactive;
  }

  if (patch?.status != null) {
    updates.status = patch.status;
  }

  if (updates.status == null) {
    logger.warn(
      '[mapActivityStatusFromPatchResponse] PATCH succeeded but response omitted status'
    );
  }

  return {...currentFormData, ...updates};
};
